import React, { useEffect, useState } from "react";
import { Modal, Button, Spin, Result, Tabs, Tooltip as AntdTooltip, Typography } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import {
    MapContainer,
    TileLayer,
    Marker,
    Popup,
    useMap,
    Polyline,
    Tooltip,
    CircleMarker,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useTrackVehicleQuery } from "./TripPlanTableNew";
import trailerActive from "../../assets/images/trailer-active.png";
// import originMarker from "../../assets/images/red-marker.png";
import originWarehouseMarker from "../../assets/images/origin-warehouse.png";
import destinationWarehouseMarker from "../../assets/images/destination-warehouse.png";

import TabPane from "antd/lib/tabs/TabPane";
import {
    GetTripPlanWaybillsByPlateNoService,
    GetVehicleTrackingHistoryService,
} from "../../services/trip-plan-service";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { TripPlanStatusEnum } from "../../utils/app-common";

export const useGetTripTrackingDetailQuery = ({
    plateNo,
    enabled,
}: {
    plateNo: string;
    enabled: boolean;
}) => {
    const onRequest = async () => {
        const res = await GetTripPlanWaybillsByPlateNoService(plateNo);
        if (res?.status === 200 && res?.data?.statusCode === 200) {
            return res.data?.data || {};
        } else {
            return { error: "Failed to track vehicle" };
        }
    };

    return useQuery({
        queryKey: ["trip-tracking", plateNo],
        queryFn: () => onRequest(),
        enabled: !!plateNo && enabled,
    });
};

export const useGetVehicleTrackingHistoryQuery = (
    request: { id: string; fromDate: string; toDate: string },
    enabled: boolean
) => {
    const fetchHistory = async () => {
        const res = await GetVehicleTrackingHistoryService(request);
        if (res?.status === 200 && res?.data?.statusCode === 200) {
            return res.data?.data?.data || [];
        } else {
            return { error: "Failed to fetch vehicle tracking history" };
        }
    };

    return useQuery({
        queryKey: ["vehicle-tracking-history", request],
        queryFn: fetchHistory,
        enabled,
    });
};

const trailerIcon = new L.Icon({
    iconUrl: trailerActive,
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -40],
});

const MapEffect: React.FC<{ lat: number; lng: number }> = ({ lat, lng }) => {
    const map = useMap();

    useEffect(() => {
        map.setView([lat, lng], 7);
    }, [lat, lng, map]);

    return null;
};

function FitBoundsOnLoad({ bounds }) {
    const map = useMap();

    useEffect(() => {
        if (bounds && bounds.length > 0) {
            map.fitBounds(bounds, { padding: [50, 50] });
        }

    }, [map, bounds]);

    return null;
}


const RecenterControl: React.FC<{
    lat: number;
    lng: number;
    refetch: () => void;
}> = ({ lat, lng, refetch }) => {
    const map = useMap();

    useEffect(() => {
        const controlDiv = L.DomUtil.create(
            "div",
            "leaflet-bar leaflet-control leaflet-control-custom"
        );
        controlDiv.style.backgroundColor = "white";
        controlDiv.style.width = "80px";
        controlDiv.style.height = "30px";
        controlDiv.style.display = "flex";
        controlDiv.style.alignItems = "center";
        controlDiv.style.justifyContent = "center";
        controlDiv.style.cursor = "pointer";
        controlDiv.title = "Recenter and Refresh";
        controlDiv.innerHTML = "Re-position ⟳ ";

        controlDiv.onclick = () => {
            map.setView([lat, lng], map.getZoom());
            refetch();
        };

        const CustomControl = L.Control.extend({
            onAdd: () => controlDiv,
            onRemove: () => { },
        });

        const controlInstance = new CustomControl({ position: "topright" });
        map.addControl(controlInstance);

        return () => {
            map.removeControl(controlInstance);
        };
    }, [lat, lng, map, refetch]);

    return null;
};

const SetMapInstance: React.FC<{ onMapReady: (map: L.Map) => void }> = ({
    onMapReady,
}) => {
    const map = useMap();

    useEffect(() => {
        onMapReady(map);
    }, [map, onMapReady]);

    return null;
};

type TrackTripProps = {
    visible: boolean;
    handleCancel: () => void;
    record?: any;
};

const formatDateTime = (ms?: number | string) => {
    if (!ms) return "";
    return moment(Number(ms)).format("YYYY-MM-DD HH:mm:ss");
};

export const TrackTripOld: React.FC<TrackTripProps> = ({
    visible,
    handleCancel,
    record,
}) => {

    const isArrivedAtDestination = ![TripPlanStatusEnum.LoadToDestination, TripPlanStatusEnum.OffloadAtTransit, TripPlanStatusEnum.LoadAtTransit].includes(record?.currentStatus);
    const req = {
        id: record?.plateNumber,
        fromDate: formatDateTime(record?.etd),
        toDate: formatDateTime(record?.ata || record?.eta),
    };
    const {
        data: tripData,
        isLoading,
        isError,
        isFetching,
        refetch,
    } = useTrackVehicleQuery({
        plateNo: record?.vehicleTrackingNumber,
        enabled: visible && !!record?.vehicleTrackingNumber,
    });

    const {
        data: tripTrackingData,
        isLoading: isTripTrackingLoading,
        isFetching: isTripTrackingFetching,
        refetch: refetchTripTracking,
    } = useGetTripTrackingDetailQuery({
        plateNo: record?.plateNumber,
        enabled: visible || !!record?.plateNumber,
    });

    const {
        data: tripRouteMapData = [],
        isLoading: isTripRouteMapLoading,
        isFetching: isTripRouteMapFetching,
        refetch: refetchTripRouteMap,
        isError: isTripRouteMapError,
        error: tripRouteMapError,
    } = useGetVehicleTrackingHistoryQuery(req, visible && !!record?.vehicleTrackingNumber);


    const [mapInstance, setMapInstance] = useState<L.Map | null>(null);
    const [activeTab, setActiveTab] = useState("1");

    useEffect(() => {
        if (visible && mapInstance) {
            const timer = setTimeout(() => {
                mapInstance.invalidateSize();
            }, 150);
            return () => clearTimeout(timer);
        }
    }, [visible, mapInstance]);

    // Interval for checkpoints on polyline (e.g., every 5 points)
    const CHECKPOINT_INTERVAL = 30;

    const renderContent = () => {
        if (!record?.vehicleTrackingNumber) {
            return (
                <Result
                    status="warning"
                    title="Tracking not available"
                    subTitle="This trip does not have tracking information."
                    extra={
                        <Button type="primary" onClick={handleCancel}>
                            Close
                        </Button>
                    }
                />
            );

        }

        if (isTripRouteMapLoading || isLoading) {
            return (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                    <Spin />
                </div>
            )
        }


        if (isTripRouteMapError || tripRouteMapData.length === 0) {
            return (
                <Result
                    status="error"
                    title={isTripRouteMapError ? "Failed to fetch trip data" : "No trip data available"}
                    subTitle="There was an issue retrieving the tracking details. Please try again."
                    extra={
                        <Button
                            type="primary"
                            icon={<ReloadOutlined />}
                            onClick={() => refetch()}
                        >
                            Retry
                        </Button>
                    }
                />
            );
        }

        if (tripRouteMapData.length > 0) {
            return (
                <div style={{ height: "100%", width: "100%" }}>
                    <MapContainer
                        center={[tripRouteMapData[tripRouteMapData?.length - 1].lat, tripRouteMapData[tripRouteMapData?.length - 1].lng]}
                        zoom={4}
                        bounds={tripRouteMapData.map(point => [point.lat, point.lng])}
                        style={{ height: "70vh", width: "100%" }}
                    >
                        <FitBoundsOnLoad bounds={tripRouteMapData.map(point => [point.lat, point.lng])} />
                        <SetMapInstance onMapReady={setMapInstance} />
                        <TileLayer
                            url="https://mt1.google.com/vt/lyrs=r&x={x}&y={y}&z={z}"
                            attribution='&copy; <a href="https://www.google.com/maps">Google Maps</a>'
                        />
                        <MapEffect lat={tripData.lat} lng={tripData.lng} />
                        <RecenterControl lat={tripData.lat} lng={tripData.lng} refetch={refresh} />

                        {/* Route line */}
                        {Array.isArray(tripRouteMapData) && tripRouteMapData.length > 0 && (
                            <>
                                <Polyline
                                    positions={tripRouteMapData.map((point) => [point.lat, point.lng])}
                                    color={isArrivedAtDestination ? "green" : "red"}
                                    weight={4}
                                    opacity={0.8}
                                />

                                {/* Checkpoint markers every CHECKPOINT_INTERVAL points */}
                                {tripRouteMapData.map((point, index) => {
                                    if (index % CHECKPOINT_INTERVAL !== 0) return null;
                                    return (
                                        <CircleMarker
                                            key={`checkpoint-${index}`}
                                            center={[point.lat, point.lng]}
                                            radius={2.5}
                                            color={isArrivedAtDestination ? "green" : "red"}      // border color
                                            fillColor="yellow"    // fill color
                                            fillOpacity={0.9}
                                            stroke={true}
                                            weight={2}
                                        >
                                            <Tooltip direction="top" offset={[0, -10]} opacity={1} permanent={false}>
                                                <div style={{ fontSize: "12px", lineHeight: "1.4" }}>
                                                    <div><b>Date:</b> {moment(point.date).format("YYYY-MM-DD HH:mm:ss")}</div>
                                                    <div><b>Odometer:</b> {point.odometer?.toFixed(2)} km</div>
                                                    <div><b>Speed:</b> {point.speed} km/h</div>
                                                    <div><b>Temp:</b> {point.temperature ?? "--"} °C</div>
                                                </div>
                                            </Tooltip>
                                        </CircleMarker>

                                    );
                                })}

                                <Marker
                                    position={[
                                        tripRouteMapData[tripRouteMapData.length - 1].lat,
                                        tripRouteMapData[tripRouteMapData.length - 1].lng,
                                    ]}
                                    icon={L.icon({
                                        iconUrl: originWarehouseMarker,
                                        iconSize: [34, 34],
                                        iconAnchor: [10, 34],
                                        popupAnchor: [0, -34],
                                    })}
                                >
                                    <Popup>
                                        {/* <div>
                                            <strong> </strong>
                                            <br />
                                            Lat: {tripRouteMapData[tripRouteMapData.length - 1].lat}
                                            <br />
                                            Lng: {tripRouteMapData[tripRouteMapData.length - 1].lng}
                                        </div> */}
                                        <Typography.Title level={5}>Trip Origin Details</Typography.Title>
                                        <div style={{ fontSize: "12px", lineHeight: "1.4" }}>
                                            <div><b>Origin:</b> {record?.originName}</div>
                                            <div><b>Date:</b> {moment(tripRouteMapData[tripRouteMapData.length - 1].date).format("YYYY-MM-DD HH:mm:ss")}</div>
                                            <div><b>Odometer:</b> {tripRouteMapData[tripRouteMapData.length - 1].odometer?.toFixed(2)} km</div>
                                            <div><b>Speed:</b> {tripRouteMapData[tripRouteMapData.length - 1].speed} km/h</div>
                                            <div><b>Temp:</b> {tripRouteMapData[tripRouteMapData.length - 1].temperature ?? "--"} °C</div>
                                        </div>
                                    </Popup>
                                </Marker>
                                {
                                    isArrivedAtDestination ?
                                        <Marker
                                            position={[
                                                tripRouteMapData[0].lat,
                                                tripRouteMapData[0].lng,
                                            ]}
                                            icon={L.icon({
                                                iconUrl: destinationWarehouseMarker,
                                                iconSize: [34, 34],
                                                iconAnchor: [10, 34],
                                                popupAnchor: [0, -34],
                                            })}
                                        >
                                            <Popup maxWidth={300} >
                                                <div style={{ width: "220px" /* adjust as needed */ }}>
                                                    <Typography.Title level={5} style={{ marginBottom: 8 }}>Trip Destination Details</Typography.Title>
                                                    <div style={{ fontSize: "12px", lineHeight: "1.4" }}>
                                                        <div><b>Destination:</b> {record?.destinationName}</div>
                                                        <div><b>Date:</b> {moment(tripRouteMapData[0].date).format("YYYY-MM-DD HH:mm:ss")}</div>
                                                        <div><b>Odometer:</b> {tripRouteMapData[0].odometer?.toFixed(2)} km</div>
                                                        <div><b>Speed:</b> {tripRouteMapData[0].speed} km/h</div>
                                                        <div><b>Temp:</b> {tripRouteMapData[0].temperature ?? "--"} °C</div>
                                                    </div>
                                                </div>
                                            </Popup>
                                        </Marker> : null
                                }


                            </>
                        )}
                        {
                            !isArrivedAtDestination && tripRouteMapData.length >= 2 ? (() => {
                                const origin = tripRouteMapData[tripRouteMapData.length - 1];
                                const recent = tripRouteMapData[0];

                                const calculateBearing = (
                                    lat1: number,
                                    lon1: number,
                                    lat2: number,
                                    lon2: number
                                ): number => {
                                    const toRad = (deg: number) => (deg * Math.PI) / 180;
                                    const toDeg = (rad: number) => (rad * 180) / Math.PI;

                                    const dLon = toRad(lon2 - lon1);
                                    const y = Math.sin(dLon) * Math.cos(toRad(lat2));
                                    const x =
                                        Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
                                        Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLon);
                                    const brng = Math.atan2(y, x);
                                    return (toDeg(brng) + 360) % 360;
                                };

                                const bearing = calculateBearing(origin.lat, origin.lng, recent.lat, recent.lng);

                                // const bearing = 0;  0 : // North (up), 90: East (right), 180: South (down), 270: West (left)
                                // Adjust the bearing to match the icon rotation logic
                                let transform = "";

                                if (bearing >= 45 && bearing < 135) {
                                    // East (right) – default
                                    transform = "rotate(0deg)";
                                } else if (bearing >= 135 && bearing < 225) {
                                    // South (down)
                                    transform = "rotate(90deg)";
                                } else if (bearing >= 225 && bearing < 315) {
                                    // West (left) – use mirror instead of rotate(180deg)
                                    transform = "scaleX(-1)";
                                } else {
                                    // North (up)
                                    transform = "rotate(-90deg)";
                                }

                                const rotatedTrailerIcon = L.divIcon({
                                    html: `<img src="${trailerActive}" style="transform: ${transform}; width:40px; height:40px;" />`,
                                    iconSize: [40, 40],
                                    className: "rotated-trailer-icon",
                                });


                                return (
                                    <Marker position={[recent.lat, recent.lng]} icon={rotatedTrailerIcon}>
                                        <Popup>
                                            <Tabs
                                                activeKey={activeTab}
                                                onChange={setActiveTab}
                                                size="small"
                                                tabBarExtraContent={
                                                    <Button
                                                        size="small"
                                                        icon={<ReloadOutlined />}
                                                        onClick={() =>
                                                            activeTab === "1" ? refetch() : refetchTripTracking()
                                                        }
                                                        style={{ marginRight: "8px" }}
                                                    ></Button>
                                                }
                                            >
                                                <TabPane tab="Fleet Detail" key="1">
                                                    <div
                                                        style={{
                                                            display: "grid",
                                                            gridTemplateColumns: "repeat(2, 1fr)",
                                                            gap: "16px",
                                                        }}
                                                    >
                                                        {[
                                                            {
                                                                label: "Fleet Plate No",
                                                                value: tripData?.vehicleId,
                                                            },
                                                            { label: "Fleet Code", value: tripData?.fleetCode },
                                                            {
                                                                label: "Vehicle Speed",
                                                                value: `${tripData?.vehicleSpeed || "--"} km/h`,
                                                            },
                                                            {
                                                                label: "Vehicle Status",
                                                                value: tripData?.vehicleStatus || "--",
                                                            },
                                                        ].map(({ label, value }) => (
                                                            <div key={label}>
                                                                <div style={{ fontWeight: "bold" }}>{label}</div>
                                                                <div>{value || "--"}</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </TabPane>

                                                <TabPane tab="Trip Detail" key="2">
                                                    {isTripTrackingFetching || isTripTrackingLoading ? (
                                                        <Spin size="small" />
                                                    ) : (
                                                        <div
                                                            style={{
                                                                maxHeight: "220px",
                                                                overflowY: "auto",
                                                                position: "relative",
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    display: "grid",
                                                                    gridTemplateColumns: "repeat(2, 1fr)",
                                                                    gap: "16px",
                                                                }}
                                                            >
                                                                {[
                                                                    {
                                                                        label: "Trip Plan Number",
                                                                        value: tripTrackingData?.tripPlanNumber,
                                                                    },
                                                                    {
                                                                        label: "Trip Code",
                                                                        value: tripTrackingData?.tripCode,
                                                                    },
                                                                    {
                                                                        label: "Origin City",
                                                                        value: tripTrackingData?.originCity,
                                                                    },
                                                                    {
                                                                        label: "Destination City",
                                                                        value: tripTrackingData?.destCity,
                                                                    },
                                                                    {
                                                                        label: "Waybill No",
                                                                        value: tripTrackingData?.waybillNo,
                                                                    },
                                                                    {
                                                                        label: "Plate Number",
                                                                        value: tripTrackingData?.plateNumber,
                                                                    },
                                                                    {
                                                                        label: "Driver Name",
                                                                        value: tripTrackingData?.driverName1,
                                                                    },
                                                                    {
                                                                        label: "Vehicle Plate No",
                                                                        value: tripTrackingData?.plateNumber,
                                                                    },
                                                                    {
                                                                        label: "ETD",
                                                                        value: tripTrackingData?.etdDate,
                                                                    },
                                                                    {
                                                                        label: "ETA",
                                                                        value: tripTrackingData?.etaDate,
                                                                    },
                                                                ].map(({ label, value }) => (
                                                                    <div key={label}>
                                                                        <div style={{ fontWeight: "bold" }}>{label}</div>
                                                                        <div>{value || "--"}</div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </TabPane>
                                            </Tabs>
                                        </Popup>
                                    </Marker>
                                );
                            })() : null
                        }



                    </MapContainer>

                    <style>{`
                .leaflet-container {
                z-index: 0;
                }
                .leaflet-control-attribution {
                display: none;
                }
                .leaflet-popup-content {
                width: 500px !important;
                max-height: 800px;
                overflow: auto;
                word-wrap: break-word;
                }
                .leaflet-popup-content-wrapper {
                padding: 10px;
                }
            `}</style>
                </div>
            );
        }



        return (
            <Result
                status="info"
                title="No location data available"
                subTitle="The trip is not yet tracked or missing coordinates."
            />
        );
    };

    const refresh = () => {
        refetch();
        refetchTripTracking();
        refetchTripRouteMap();
    };

    return (
        <Modal
            title={
                <div style={{ display: "flex", alignItems: "center" }}>
                    <span style={{ marginRight: "8px" }}>
                        Trip Tracking Details <b>({record?.tripPlanNumber})</b>
                    </span>
                    <Button
                        loading={isLoading || isFetching}
                        icon={<ReloadOutlined />}
                        size="small"
                        onClick={refresh}
                    >
                        Refresh
                    </Button>
                </div>
            }
            open={visible}
            onCancel={handleCancel}
            footer={[
                <Button key="close" onClick={handleCancel}>
                    Close
                </Button>,
            ]}
            width={1100}
            style={{ top: 20 }}
            maskClosable={false}
            destroyOnClose={true}
        >

            {renderContent()}
        </Modal>
    );
};
